import { getImagesSizes } from '~/utils/image-sizes-utils';
import { isLibraryVideo } from '@modules/product/util/is-library-video';
import type {
    LibraryDiagram,
    LibraryImage,
    LibraryVideo,
} from '@modules/product/type/product-gallery-types';

/**
 * The gallery row's arithmetic, shared by the gallery itself and by the foot of
 * the page that grows the next product's row into place. One definition, so a
 * slide the foot lays out is the width and height the gallery then paints it
 * at, to the pixel: the page turn under the grown row only stays invisible
 * while nothing about the row changes.
 */

/**
 * Responsive sizes for a gallery slide — 93.6rem (≈1498px at the 1920 anchor)
 * on desktop, 22.2525rem (≈356px at the 414 anchor) on mobile. Shared with the
 * route's preload link so the browser reuses the preloaded first slide instead
 * of fetching a second variant (the cause of the grey flash on load).
 */
export const PRODUCT_GALLERY_SLIDE_SIZES = getImagesSizes({
    desktop: 1498,
    mobile: 414,
});

/**
 * Slides share a fixed height and take their width from the canonical shape the
 * image snaps to (variable-width, consistent-height row). 54.375rem ≈ 870px at
 * our 1rem = 16px base.
 */
export const GALLERY_SLIDE_HEIGHT_REM = 54.375;

/**
 * The padding a slide carries on each side on desktop, which separates it from
 * its neighbours. Emitted as `--g-slide-pad` so the CarouselItem and the page
 * gutter the bounded rail rests on read one value rather than each restating
 * the number.
 */
export const GALLERY_SLIDE_PAD_REM = 0.375;

/**
 * Both of a slide's gutters, so the track measures the same as the row the
 * browser lays out — and so an offset that centres a slide measures the padded
 * box embla centres.
 */
export const GALLERY_SLIDE_GUTTER_REM = GALLERY_SLIDE_PAD_REM * 2;

/**
 * Mobile cards, sized in vw so each sits a little inside the viewport and the
 * next slide peeks at the edge (a single slide instead stretches to the page
 * gutter). Every card is the same square whatever the image's shape — a row of
 * differing widths read as a broken carousel, and snapping one card at a time
 * wants one card width. Photos cover-crop into it; clear-cut renders gain
 * canvas (see the gallery `fit`).
 */
export const GALLERY_SLIDE_HEIGHT_MOBILE_VW = 84;

/** Mobile card width: square, the same as its height. */
export const GALLERY_SLIDE_WIDTH_MOBILE_VW = GALLERY_SLIDE_HEIGHT_MOBILE_VW;

/**
 * Each product image is served in one of three canonical shapes by orientation —
 * portrait 2:3, square 1:1, landscape 3:2 — so the gallery shows at most three
 * slide widths. The thresholds + ratios must match `getCanonicalProductRatio` on
 * the server so the CSS box matches the rendition (no letterbox/crop). Videos
 * aren't product images (no snap rendition), so they keep a native 16:9 frame.
 */
const GALLERY_PORTRAIT_RATIO = 2 / 3;
const GALLERY_SQUARE_RATIO = 1;
const GALLERY_LANDSCAPE_RATIO = 3 / 2;
const GALLERY_LANDSCAPE_THRESHOLD = 1.25;
const GALLERY_PORTRAIT_THRESHOLD = 0.833;
export const GALLERY_VIDEO_RATIO = 16 / 9;

/** The canonical display ratio (width ÷ height) an image of `aspectRatio` snaps to. */
export const getCanonicalRatio = (
    aspectRatio: number | null | undefined,
): number => {
    const ratio = aspectRatio || 1;
    if (ratio >= GALLERY_LANDSCAPE_THRESHOLD) return GALLERY_LANDSCAPE_RATIO;
    if (ratio <= GALLERY_PORTRAIT_THRESHOLD) return GALLERY_PORTRAIT_RATIO;
    return GALLERY_SQUARE_RATIO;
};

/** The canonical display ratio (width ÷ height) a gallery slide snaps to. */
export const getSlideRatio = (
    slide: LibraryImage | LibraryVideo | LibraryDiagram,
): number => {
    if (isLibraryVideo(slide)) return GALLERY_VIDEO_RATIO;
    return getCanonicalRatio(slide.AspectRatio);
};

/**
 * The desktop offset the carousel track rests at, as a CSS `translateX` value.
 *
 * Normally that is "centre slide 0" — `(viewport - slide0) / 2` — which is also
 * where embla parks a centre-aligned first slide, so the SSR row is already in
 * position when embla hydrates. When every slide fits across the viewport there
 * is nothing to scroll to, and centring slide 0 strands the rest of the row off
 * to one side; the whole track is centred instead, at `(viewport - track) / 2`.
 *
 * Both cases are one expression so the row is right before any JS runs. The
 * clamp is the conditional: `viewport - track` is positive only while the row
 * fits, and scaling it far past the inset saturates the clamp to either end —
 * the full inset (shifting slide-0 centring onto the track) or nothing.
 */
export const galleryTrackOffset = (slideZeroRem: number, trackRem: number) => {
    const inset = (trackRem - slideZeroRem) / 2;
    return `calc((100% - ${slideZeroRem}rem) / 2 - clamp(0px, (100% - ${trackRem}rem) * 1000, ${inset}rem))`;
};

/**
 * The viewport, in rem, at the design frame. The root font-size is `0.833vw`
 * until it caps at 16px, so every window up to 1920 is exactly this many rem
 * across — which is what lets the slide arithmetic answer "does this row fit"
 * on the server, before there is a viewport to measure.
 */
export const GALLERY_VIEWPORT_REM = 120;

/**
 * Whether the gallery loops this row.
 *
 * A looping row is what the reader sees on the page: the last slides wrapped
 * round to the left of the centred first one. A row that fits the viewport is
 * centred whole instead, and nothing wraps.
 */
export const galleryRowLoops = (slideCount: number, trackRem: number) =>
    slideCount > 1 && trackRem > GALLERY_VIEWPORT_REM;
